import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import {
  toYearParam,
  useMsDisputeParams,
  useMsPayslipParams,
} from "./params";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Check, Clock, Download, Search } from "lucide-react";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { ViewAction } from "@/components/table/actions";
import { Table, type ColumnDef } from "@/components/table";
import { PageDataStats } from "@/components/PageDataStats";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { cn } from "@/utils/cn";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import {
  useGetEmployeePayrollSummaryQuery,
  useListEmployeePayslipsQuery,
  type IEmployeePayslipListItem,
} from "@/services/(my-space)/employeePayrollApi";
import { useDownloadEmployeePayslip } from "@/services/(my-space)/employeePayslipDownload";
import { EmployeePayrollStatusEnum } from "@/utils/enums/payroll.enum";
import { MySpacePayslipDetail } from "./payslip";

const STATUS_ALL = "all" as const;
const YEAR_ALL = "all" as const;

const buildStatusLabel = (
  translate: (key: string) => string,
): Record<EmployeePayrollStatusEnum, string> => ({
  [EmployeePayrollStatusEnum.PAID]: translate("ui.paid"),
  [EmployeePayrollStatusEnum.PROCESSED]: translate("ui.processed"),
  [EmployeePayrollStatusEnum.APPROVED]: translate("labels.approved"),
  [EmployeePayrollStatusEnum.HOLD]: translate("ui.hold"),
  [EmployeePayrollStatusEnum.EXCLUDED]: translate("ui.excluded"),
  [EmployeePayrollStatusEnum.FAILED]: translate("ui.failed"),
});

const buildStatusOptions = (
  translate: (key: string) => string,
): {
  value: typeof STATUS_ALL | EmployeePayrollStatusEnum;
  label: string;
}[] => [
  { value: STATUS_ALL, label: translate("ui.all_statuses") },
  {
    value: EmployeePayrollStatusEnum.PAID,
    label: buildStatusLabel(translate)[EmployeePayrollStatusEnum.PAID],
  },
  {
    value: EmployeePayrollStatusEnum.PROCESSED,
    label: buildStatusLabel(translate)[EmployeePayrollStatusEnum.PROCESSED],
  },
  {
    value: EmployeePayrollStatusEnum.APPROVED,
    label: buildStatusLabel(translate)[EmployeePayrollStatusEnum.APPROVED],
  },
  {
    value: EmployeePayrollStatusEnum.HOLD,
    label: buildStatusLabel(translate)[EmployeePayrollStatusEnum.HOLD],
  },
  {
    value: EmployeePayrollStatusEnum.EXCLUDED,
    label: buildStatusLabel(translate)[EmployeePayrollStatusEnum.EXCLUDED],
  },
  {
    value: EmployeePayrollStatusEnum.FAILED,
    label: buildStatusLabel(translate)[EmployeePayrollStatusEnum.FAILED],
  },
];

const buildSortOptions = (
  translate: (key: string) => string,
): {
  value: string;
  label: string;
  sort: string;
  sortOrder: "asc" | "desc";
}[] => [
  {
    value: "periodStart-desc",
    label: translate("ui.newest_first"),
    sort: "periodStart",
    sortOrder: "desc",
  },
  {
    value: "periodStart-asc",
    label: translate("ui.oldest_first"),
    sort: "periodStart",
    sortOrder: "asc",
  },
  {
    value: "netSalary-desc",
    label: translate("ui.highest_net_pay"),
    sort: "netSalary",
    sortOrder: "desc",
  },
  {
    value: "netSalary-asc",
    label: translate("ui.lowest_net_pay"),
    sort: "netSalary",
    sortOrder: "asc",
  },
  {
    value: "payDate-desc",
    label: translate("ui.recent_pay_date"),
    sort: "payDate",
    sortOrder: "desc",
  },
  {
    value: "payDate-asc",
    label: translate("ui.earliest_pay_date"),
    sort: "payDate",
    sortOrder: "asc",
  },
];

const formatAmount = (v: number | string, currency?: string) => {
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  if (!Number.isFinite(n)) return "—";
  const amt = n.toLocaleString(undefined, {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${amt}` : amt;
};

const MoneyCell = ({
  value,
  currency,
  strong,
  dashIfZero,
}: {
  value: number | string;
  currency?: string | null;
  strong?: boolean;
  dashIfZero?: boolean;
}) => {
  const n = typeof value === "string" ? Number(value.replace(/,/g, "")) : value;
  if (!Number.isFinite(n) || (dashIfZero && n === 0)) {
    return <span className="text-gray-300">—</span>;
  }
  return (
    <span className="inline-flex items-baseline gap-1">
      {currency && (
        <span className="text-[10px] font-medium text-gray-400">
          {currency}
        </span>
      )}
      <span
        className={cn(
          "text-sm",
          strong ? "font-semibold text-gray-900" : "text-gray-700",
        )}
      >
        {formatAmount(n)}
      </span>
    </span>
  );
};

const ReceiptBadge = ({ status }: { status: "signed" | "pending" | null }) => {
  const { t: translate } = useTranslation("my-space");
  if (status === "signed")
    return (
      <span className="inline-flex items-center gap-1 rounded-xs bg-success-100/10 text-success-100 px-2 py-0.5 text-[11px] font-semibold">
        <Check size={11} />
        {translate("ui.confirmed")}
      </span>
    );
  if (status === "pending")
    return (
      <span className="inline-flex items-center gap-1 rounded-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 text-[11px] font-semibold">
        <Clock size={11} />
        {translate("ui.awaiting")}
      </span>
    );
  return <span className="text-gray-300 text-[11px]">—</span>;
};

const safeDate = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : format(d, "dd/MM/yyyy");
};

const buildYearOptions = (translate: (key: string) => string) => {
  const current = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => ({
    value: String(current - i),
    label: String(current - i),
  }));
  return [{ value: YEAR_ALL, label: translate("ui.all_years") }, ...years];
};

type MsPayrollTab = "payslips" | "disputes";

export const MySpacePayroll = () => {
  const { t: translate } = useTranslation("my-space");
  usePageTitle(withBranding(translate("page_titles.payroll")));

  const yearOptions = useMemo(() => buildYearOptions(translate), [translate]);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = (searchParams.get("tab") as MsPayrollTab) ?? "payslips";
  const urlDisputeId = searchParams.get("disputeId");
  const urlPayslipId = searchParams.get("payslipId");
  const tab: MsPayrollTab = urlTab === "disputes" ? "disputes" : "payslips";
  const setTab = (next: MsPayrollTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === "payslips") {
      params.delete("tab");
    } else {
      params.set("tab", next);
    }
    params.delete("disputeId");
    setSearchParams(params, { replace: true });
  };
  const setDisputeId = (id: number | null) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", "disputes");
    if (id == null) params.delete("disputeId");
    else params.set("disputeId", String(id));
    setSearchParams(params, { replace: true });
  };
  const setPayslipId = (id: number | null) => {
    const params = new URLSearchParams(searchParams);
    params.delete("tab");
    params.delete("disputeId");
    if (id == null) params.delete("payslipId");
    else params.set("payslipId", String(id));
    setSearchParams(params, { replace: true });
  };
  // Search, filters and page all live in the URL, namespaced per tab — the
  // payslips and disputes tables share one url. See useTableParams.
  const tp = useMsPayslipParams();
  const { year, status, sortKey } = tp.filters;

  const activeSort =
    buildSortOptions(translate).find((o) => o.value === sortKey) ??
    buildSortOptions(translate)[0];

  const selectedYear = toYearParam(year);

  const { data: summaryResponse, isFetching: isFetchingSummary } =
    useGetEmployeePayrollSummaryQuery({
      year: selectedYear ?? new Date().getFullYear(),
    });
  const summary = summaryResponse?.data;

  const { currentData, isFetching } = useListEmployeePayslipsQuery({
    ...(selectedYear ? { year: selectedYear } : {}),
    page: tp.page,
    limit: 20,
    sort: activeSort.sort,
    sortOrder: activeSort.sortOrder,
    ...(tp.committedSearch ? { search: tp.committedSearch } : {}),
    ...(status !== STATUS_ALL
      ? { status: status as EmployeePayrollStatusEnum }
      : {}),
  });

  const rows = useMemo<IEmployeePayslipListItem[]>(
    () => currentData?.data ?? [],
    [currentData],
  );
  const totalPages =
    currentData?.meta?.totalPages ?? currentData?.meta?.lastPage ?? 1;

  const { download } = useDownloadEmployeePayslip();

  const placeholder = "—";
  const statsData = [
    {
      name: translate("ui.current_month_net_salary"),
      value: summary
        ? `${summary.currentMonthNetSalary.currency} ${formatAmount(summary.currentMonthNetSalary.amount)}`
        : placeholder,
      icon: "/icons/interface-icons/stats/payroll/icon-3d-cash-envelop.png",
    },
    {
      name: translate("ui.total_earnings_this_year"),
      value: summary
        ? `${summary.totalEarningsThisYear.currency} ${formatAmount(summary.totalEarningsThisYear.amount)}`
        : placeholder,
      icon: "/icons/interface-icons/stats/payroll/icon-3d-coin-pot.png",
    },
    {
      name: translate("ui.deductions_this_month"),
      value: summary
        ? `${summary.deductionsThisMonth.currency} ${formatAmount(summary.deductionsThisMonth.amount)}`
        : placeholder,
      icon: "/icons/interface-icons/stats/payroll/icon-3d-cal-percentage.png",
    },
    {
      name: translate("ui.last_payment_status"),
      value: summary
        ? (buildStatusLabel(translate)[summary.lastPaymentStatus] ??
          summary.lastPaymentStatus)
        : placeholder,
      icon: "/icons/interface-icons/stats/payroll/icon-3d-card-settings.png",
    },
  ];

  const columns: ColumnDef<IEmployeePayslipListItem>[] = [
    {
      key: "period",
      label: translate("ui.pay_period"),
      className: "w-[12%]",
      render: (r) => (
        <span className="text-sm font-medium text-gray-900">{r.payPeriod}</span>
      ),
    },
    {
      key: "gross",
      label: translate("ui.gross_salary"),
      className: "w-[12%]",
      render: (r) => (
        <MoneyCell value={r.grossSalary} currency={r.currency?.code} />
      ),
    },
    {
      key: "overtime",
      label: translate("ui.overtime"),
      className: "w-[10%]",
      render: (r) => (
        <MoneyCell
          value={r.overtimePay}
          currency={r.currency?.code}
          dashIfZero
        />
      ),
    },
    {
      key: "deductions",
      label: translate("labels.deductions"),
      className: "w-[10%]",
      render: (r) => (
        <MoneyCell value={r.deductions} currency={r.currency?.code} />
      ),
    },
    {
      key: "contribution",
      label: translate("labels.contribution"),
      className: "w-[10%]",
      render: (r) => (
        <MoneyCell
          value={r.contribution}
          currency={r.currency?.code}
          dashIfZero
        />
      ),
    },
    {
      key: "net",
      label: translate("ui.net_salary"),
      className: "w-[12%]",
      render: (r) => (
        <MoneyCell value={r.netSalary} currency={r.currency?.code} strong />
      ),
    },
    {
      key: "payDate",
      label: translate("ui.pay_date"),
      className: "w-[10%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{safeDate(r.payDate)}</span>
      ),
    },
    {
      key: "status",
      label: translate("labels.status"),
      className: "w-[10%]",
      render: (r) => {
        const s = r.status as EmployeePayrollStatusEnum;
        return (
          <Badge variant={statusToVariant(s)}>
            {buildStatusLabel(translate)[s] ?? r.status}
          </Badge>
        );
      },
    },
    {
      key: "receipt",
      label: translate("ui.receipt"),
      className: "w-[9%]",
      render: (r) => <ReceiptBadge status={r.receiptSignatureStatus} />,
    },
    {
      key: "actions",
      label: translate("labels.actions"),
      className: "w-[12%]",
      render: (r) => (
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="text-primary gap-1"
            disabled={!r.hasPayslip}
            title={translate("payroll.download_payslip")}
            onClick={() => download(r.id)}
          >
            <Download size={15} />
          </Button>
          <ViewAction
            tooltip={translate("payroll.view_payslip")}
            disabled={!r.hasPayslip}
            onClick={() => setPayslipId(r.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageDataStats data={statsData} loading={isFetchingSummary} />

      <div className="flex items-center gap-6 border-b border-border">
        {[
          { value: "payslips", label: translate("ui.payslips") },
          { value: "disputes", label: translate("ui.my_disputes") },
        ].map((t) => {
          const isActive = tab === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value as MsPayrollTab)}
              className={cn(
                "relative -mb-px py-2 text-sm font-medium transition-colors",
                isActive
                  ? "text-gray-900 border-b-2 border-primary"
                  : "text-gray-400 hover:text-gray-600",
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "disputes" && (
        <MySpaceDisputesInline
          disputeId={urlDisputeId ? Number(urlDisputeId) : null}
          onSelectDispute={setDisputeId}
        />
      )}

      {tab === "payslips" && urlPayslipId && (
        <MySpacePayslipDetail
          payslipIdProp={Number(urlPayslipId)}
          onBackInline={() => setPayslipId(null)}
        />
      )}

      {tab === "payslips" && !urlPayslipId && (
        <div className="rounded-xs border border-border bg-white flex flex-col gap-4 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 border border-input rounded-xs h-9 px-3 w-80 bg-white">
                <Search size={14} className="text-gray-400" />
                <input
                  type="text"
                  value={tp.search}
                  onChange={(e) => tp.setSearch(e.target.value)}
                  placeholder={translate("payroll.search_period")}
                  className="flex-1 bg-transparent text-xs placeholder:text-gray-400 focus:outline-hidden"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={status}
                onValueChange={(v) => tp.setFilter("status", v)}
              >
                <SelectTrigger className="h-9 w-36 bg-white text-xs font-medium">
                  <SelectValue placeholder={translate("shared.status")} />
                </SelectTrigger>
                <SelectContent>
                  {buildStatusOptions(translate).map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={sortKey}
                onValueChange={(v) => tp.setFilter("sortKey", v)}
              >
                <SelectTrigger className="h-9 w-44 bg-white text-xs font-medium">
                  <SelectValue placeholder={translate("payroll.sort")} />
                </SelectTrigger>
                <SelectContent>
                  {buildSortOptions(translate).map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-gray-500">
                {translate("payroll.select_year")}
              </span>
              <Select
                value={year}
                onValueChange={(v) => tp.setFilter("year", v)}
              >
                <SelectTrigger className="h-9 w-24 bg-white text-xs font-medium">
                  <SelectValue placeholder={translate("payroll.year")} />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Table<IEmployeePayslipListItem>
            noPadding
            columns={columns}
            data={rows}
            loading={isFetching}
            pagination={{
              totalPages,
              currentPage: tp.page,
              onPageChange: tp.setPage,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MySpacePayroll;

// ── My Disputes inline tab ────────────────────────────────────────────────────
import { ChevronLeft } from "lucide-react";
import {
  useGetMyDisputeQuery,
  useListMyDisputesQuery,
  type DisputeStatus,
  type IDispute,
} from "@/services/(company)/payrollDisputesApi";
import { DisputeDetailCard } from "@/components/payroll-disputes/DisputeDetailCard";
import { RaiseDisputePicker } from "./_components/RaiseDisputePicker";

const DISPUTE_STATUS_ALL = "all" as const;
const buildDisputeStatusOptions = (
  translate: (key: string) => string,
): {
  value: typeof DISPUTE_STATUS_ALL | DisputeStatus;
  label: string;
}[] => [
  { value: DISPUTE_STATUS_ALL, label: translate("ui.all_statuses_x") },
  { value: "PENDING", label: translate("ui.pending") },
  { value: "RESOLVED", label: translate("ui.resolved") },
  { value: "REJECTED", label: translate("labels.rejected") },
];

const DISPUTE_STATUS_TONE: Record<DisputeStatus, string> = {
  PENDING: "bg-yellow-light-3 text-yellow-700",
  UNDER_REVIEW: "bg-blue-50 text-blue-700",
  RESOLVED: "bg-success-100/10 text-success-100",
  REJECTED: "bg-red-50 text-red-600",
};

const disputeStatusLabel = (
  status: DisputeStatus,
  translate: (key: string) => string,
) => {
  const KEY: Record<DisputeStatus, string> = {
    PENDING: "ui.pending",
    UNDER_REVIEW: "company:payrolls.under_review",
    RESOLVED: "ui.resolved",
    REJECTED: "labels.rejected",
  };
  return translate(KEY[status]);
};

interface MySpaceDisputesInlineProps {
  disputeId: number | null;
  onSelectDispute: (id: number | null) => void;
}

const MySpaceDisputesInline = ({
  disputeId,
  onSelectDispute,
}: MySpaceDisputesInlineProps) => {
  if (disputeId) {
    return (
      <DisputeInlineDetail
        disputeId={disputeId}
        onBack={() => onSelectDispute(null)}
      />
    );
  }
  return <DisputesList onSelectDispute={onSelectDispute} />;
};

const DisputesList = ({
  onSelectDispute,
}: {
  onSelectDispute: (id: number) => void;
}) => {
  // `tp` for params: `t`/`translate` is the translator in this file.
  const { t: translate } = useTranslation("my-space");
  const tp = useMsDisputeParams();
  const status = tp.filters.status;
  const [raiseOpen, setRaiseOpen] = useState(false);

  const { data, isFetching } = useListMyDisputesQuery({
    page: tp.page,
    limit: 20,
    ...(status !== DISPUTE_STATUS_ALL
      ? { status: status as DisputeStatus }
      : {}),
  });

  const disputes = useMemo(() => data?.data ?? [], [data]);

  const columns: ColumnDef<IDispute>[] = [
    {
      key: "period",
      label: translate("labels.period"),
      className: "w-[16%]",
      render: (r) => (
        <span className="text-sm font-medium text-gray-900">
          {r.payrollRun?.periodStart
            ? format(new Date(r.payrollRun.periodStart), "MMM yyyy")
            : "—"}
        </span>
      ),
    },
    {
      key: "category",
      label: translate("shared.category"),
      className: "w-[18%]",
      render: (r) => (
        <span className="text-sm text-gray-700 capitalize">
          {translate(
            `company:payrolls.dispute_category_${r.category.toLowerCase()}`,
          )}
        </span>
      ),
    },
    {
      key: "lines",
      label: translate("ui.lines"),
      className: "w-[10%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{r.lines.length}</span>
      ),
    },
    {
      key: "diff",
      label: translate("ui.net_diff"),
      className: "w-[14%]",
      render: (r) => (
        <span
          className={cn(
            "text-sm font-semibold",
            r.totalProposedDiff > 0
              ? "text-success-100"
              : r.totalProposedDiff < 0
                ? "text-red-500"
                : "text-gray-700",
          )}
        >
          {r.totalProposedDiff > 0 ? "+" : ""}
          {r.totalProposedDiff.toFixed(2)}
        </span>
      ),
    },
    {
      key: "status",
      label: translate("ui.dispute_status"),
      className: "w-[12%]",
      render: (r) => (
        <span
          className={cn(
            "text-[11px] font-semibold px-2 py-0.5 rounded-xs",
            DISPUTE_STATUS_TONE[r.status],
          )}
        >
          {disputeStatusLabel(r.status, translate)}
        </span>
      ),
    },
    {
      key: "paymentStatus",
      label: translate("ui.payment_status"),
      className: "w-[12%]",
      render: (r) => {
        if (r.wpsSettledAt) {
          return (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-xs bg-success-100/10 text-success-100">
              {translate("ui.settled")}
            </span>
          );
        }
        if (r.status === "RESOLVED") {
          return (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-xs bg-yellow-light-3 text-yellow-700">
              {translate("ui.pending_payment")}
            </span>
          );
        }
        return <span className="text-[11px] text-gray-400">—</span>;
      },
    },
    {
      key: "createdAt",
      label: translate("ui.raised_on"),
      className: "w-[12%]",
      render: (r) => (
        <span className="text-xs text-gray-500">
          {format(new Date(r.createdAt), "dd MMM yyyy")}
        </span>
      ),
    },
    {
      key: "action",
      label: translate("labels.actions"),
      className: "w-[10%]",
      render: (r) => (
        <ViewAction
          tooltip={translate("payroll.view_payslip")}
          onClick={() => onSelectDispute(r.id)}
        />
      ),
    },
  ];

  return (
    <div className="rounded-xs border border-borderSecondary bg-white flex flex-col gap-4 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span className="text-sm text-gray-500">
          {translate("ui.track_your_raised_payroll_disputes")}
        </span>
        <div className="flex items-center gap-2">
          <Select
            value={status}
            onValueChange={(v) => tp.setFilter("status", v)}
          >
            <SelectTrigger className="h-9 w-44 bg-white text-xs font-medium">
              <SelectValue placeholder={translate("shared.status")} />
            </SelectTrigger>
            <SelectContent>
              {buildDisputeStatusOptions(translate).map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="gap-1 h-9"
            onClick={() => setRaiseOpen(true)}
          >
            <AlertCircle size={14} />
            {translate("ui.raise_dispute")}
          </Button>
        </div>
      </div>

      <RaiseDisputePicker
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onSuccess={() => {
          setRaiseOpen(false);
          tp.setPage(1);
        }}
      />

      <Table<IDispute>
        noPadding
        columns={columns}
        data={disputes}
        loading={isFetching}
        emptyTitle={translate("ui.no_disputes_raised")}
        emptyMessage={translate("ui.disputes_you_raise_will_appear_here")}
        pagination={{
          currentPage: tp.page,
          totalPages: data?.meta?.totalPages ?? 1,
          onPageChange: tp.setPage,
        }}
      />
    </div>
  );
};

const DISPUTE_DETAIL_TONE: Record<DisputeStatus, string> = {
  PENDING: "bg-yellow-light-3 text-yellow-700",
  UNDER_REVIEW: "bg-blue-50 text-blue-700",
  RESOLVED: "bg-success-100/10 text-success-100",
  REJECTED: "bg-red-50 text-red-600",
};

const DisputeInlineDetail = ({
  disputeId,
  onBack,
}: {
  disputeId: number;
  onBack: () => void;
}) => {
  const { t: translate } = useTranslation("my-space");
  const { data, isLoading } = useGetMyDisputeQuery(disputeId);
  const dispute = data?.data;

  if (isLoading) {
    return (
      <div className="rounded-xs border border-borderSecondary bg-white p-8 text-sm text-gray-400 text-center">
        {translate("ui.loading_dispute")}
      </div>
    );
  }
  if (!dispute) {
    return (
      <div className="rounded-xs border border-borderSecondary bg-white p-8 text-sm text-gray-400 text-center">
        {translate("ui.dispute_not_found")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 rounded-xs border border-borderSecondary bg-white px-5 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="border border-card-background rounded-xs p-2 w-fit h-fit shrink-0"
          >
            <ChevronLeft className="text-primary size-5 rtl-flip" />
          </button>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900">
              {translate("my-space:ui.dispute_number", { id: dispute.id })}
            </span>
            <span className="text-xs text-gray-400">
              {dispute.payrollRun?.periodStart
                ? translate("my-space:ui.period_raised_on", {
                    period: format(
                      new Date(dispute.payrollRun.periodStart),
                      "MMM yyyy",
                    ),
                    date: format(new Date(dispute.createdAt), "dd MMM yyyy"),
                  })
                : translate("my-space:ui.raised_on_date", {
                    date: format(new Date(dispute.createdAt), "dd MMM yyyy"),
                  })}
            </span>
          </div>
        </div>
        <span
          className={cn(
            "text-[11px] font-semibold px-2 py-1 rounded-xs",
            DISPUTE_DETAIL_TONE[dispute.status],
          )}
        >
          {disputeStatusLabel(dispute.status, translate)}
        </span>
      </div>
      <DisputeDetailCard dispute={dispute} />
    </div>
  );
};
